import DashMedia from './media/dash';
import HlsMedia from './media/hls';
import HTML5Media from './media/html5';
import * as source from './utils/media';

class Media {

    videoEl!: HTMLVideoElement;
    media!: HlsMedia | HTML5Media | DashMedia;
    mediaFiles = [];
    promisePlay = null;
    options = null;

    autoplay = false;
    mediaLoaded = false;

    constructor(videoEl: HTMLVideoElement, options: any, autoplay = true) {
        this.videoEl = videoEl;
        //this.mediaFiles = this._getMediaFiles();
        this.autoplay = autoplay;
        return this;
    }

    canPlayType(mimeType: string) {
        return this.media.canPlayType(mimeType);
    }

    load() {
        if (!this.mediaFiles.length) {
            throw new TypeError('Media not set');
        }
        this.loaded = true;
        // Remove previous media if any is detected and it's different from current one
        if (this.media && typeof this.media.destroy === 'function') {
            const sameMedia = this.mediaFiles.length === 1 && this.mediaFiles[0].src === this.media.media.src;
            if (!sameMedia) {
                this.media.destroy();
            }
        }

        // Loop until first playable source is found
        this.mediaFiles.some(media => {
            try {
                this.media = this._invoke(media);
            } catch (e) {
                //this.media = new HTML5Media(this.videoEl, media);
            }

            // If not valid, make one last attempt to check if it plays with native HTML5
            // const canPlay = this.canPlayType(media.type);
            // if (!canPlay) {
            //     this.media = new HTML5Media(this.videoEl, media);
            //     return this.canPlayType(media.type);
            // }
            return true;
        });

        try {
            if (this.media === null) {
                throw new TypeError('Media cannot be played with any valid media type');
            }

            this.media.promise.then(() => {
                this.media.load();
            });
        } catch (e) {
            // destroy media
            this.media.destroy();
            throw e;
        }
    }

    play() {
        if (!this.loaded) {
            this.load();
        }

        // Wait until any other Promise is resolved to execute the Play action
        this.promisePlay = new Promise(resolve => {
            resolve();
        }).then(this.media.promise.then(() => {
            this.media.play();
            this.promisePlay = null;
        }));

        return this.promisePlay;
    }

    pause() {
        if (!this.media) {
            return;
        }
        if (this.promisePlay) {
            this.promisePlay.then(() => {
                this.media.pause();
            });
        } else {
            this.media.pause();
        }
    }

    destroy() {
        if (!this.media) {
            return;
        }
        this.media.destroy();
        this.media = null;
    }

    set src(media) {
        this.mediaFiles = [];
        this.mediaFiles = this._getMediaFiles(media);
        this.options = media.options;
        // if (Array.isArray(media)) {
        //     this.mediaFiles = media;
        // } else if (typeof media === 'object') {
        //     this.mediaFiles.push(media);
        // }

        // this.mediaFiles.some(file => {
        //     return this.canPlayType(file.type);
        // });

        //this.videoEl.src = this.mediaFiles[0].src;
        //this.media.src = this.mediaFiles[0];
    }

    get src() {
        return this.mediaFiles;
    }

    set volume(value) {
        this.media.volume = value;
    }

    get volume() {
        return this.media ? this.media.volume : this.videoEl.volume;
    }

    set muted(value) {
        this.media.muted = value;
    }

    get muted() {
        return this.media ? this.media.muted : this.videoEl.muted;
    }

    set playbackRate(value) {
        this.media.playbackRate = value;
    }

    get playbackRate() {
        return this.media ? this.media.playbackRate : this.videoEl.playbackRate;
    }

    set defaultPlaybackRate(value) {
        this.media.defaultPlaybackRate = value;
    }

    get defaultPlaybackRate() {
        return this.media ? this.media.defaultPlaybackRate : this.videoEl.defaultPlaybackRate;
    }

    set currentTime(value) {
        this.media.currentTime = value;
    }

    get currentTime() {
        return this.media ? this.media.currentTime : this.videoEl.currentTime;
    }

    get duration() {
        const duration = this.media ? this.media.duration : this.videoEl.duration;
        // To seek backwards in a live streaming (mobile devices)
        if (duration === Infinity && this.videoEl.seekable && this.videoEl.seekable.length) {
            return this.videoEl.seekable.end(0);
        }
        return duration;
    }

    get paused() {
        return this.media ? this.media.paused : this.videoEl.paused;
    }

    get ended() {
        return this.media ? this.media.ended : this.videoEl.ended;
    }

    set loaded(loaded) {
        this.mediaLoaded = loaded;
    }

    get loaded() {
        return this.mediaLoaded;
    }

    set level(value) {
        this.media.level = value;
    }

    get level() {
        return this.media ? this.media.level : -1;
    }

    get levels() {
        return this.media ? this.media.levels : [];
    }

    _getMediaFiles(media) {
        const mediaFiles = [];
        if (media && media.src) {
            mediaFiles.push({
                src: media.src,
                type: media.type
            });
        }

        if (!mediaFiles.length) {
            mediaFiles.push({
                src: '',
                type: source.predictType(''),
            });
        }

        return mediaFiles;
    }

    _invoke(media) {
        const startTime = this.options.startTime;
        let playHLSNatively = this.videoEl.canPlayType('application/vnd.apple.mpegurl') ||
            this.videoEl.canPlayType('application/vnd.apple.mpegurl');
        if (!this.options.useNative) {
            playHLSNatively = false;
        }
        if (!playHLSNatively && source.isHlsSource(media)) {
            const hlsOptions = this.options && this.options.hls ? this.options.hls : undefined;
            return new HlsMedia(this.videoEl, media, this.autoplay, hlsOptions, startTime);
        } else if (source.isDashSource(media)) {
            const dashOptions = this.options && this.options.dash ? this.options.dash : undefined;
            return new DashMedia(this.videoEl, media, dashOptions, startTime);
        }
        return new HTML5Media(this.videoEl, media, startTime);
    }
}

export default Media;
